import React, { useState } from "react";
import { usePageMeta } from "@/lib/usePageMeta";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { ErrorState } from "@/components/StateViews";
import { ListPagination } from "@/components/ListPagination";
import { FilterChip } from "@/components/ListLayout";
import { useI18n } from "@/contexts/I18nContext";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { MessageSquare, ArrowUp, Plus } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function Discussions() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const sort = params.get("sort") || "recent";
  const query = params.get("q") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const [q, setQ] = useState(query);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });

  const qs = new URLSearchParams({ sort, page: String(page), limit: String(PAGE_SIZE) });
  if (query) qs.set("q", query);
  const { status, data, reload } = useFetch(`/threads?${qs}`);
  usePageMeta({ title: t("discussions_title"), canonical: page > 1 ? `/discussions?page=${page}` : "/discussions", noindex: !!query });
  const items = data?.items || [];

  // Search, sort and page live in the URL so back/forward and shared links work.
  const setQuery = (next) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    if (!("page" in next)) p.delete("page");
    setParams(p);
  };

  const submit = async () => {
    if (!user) { toast.error(t("login_required_short")); return; }
    try {
      const r = await api.post("/threads", form);
      setOpen(false); setForm({ title: "", body: "" });
      nav(`/discussions/${r.data.id}`);
    } catch { toast.error(t("error_title")); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
      <header className="flex flex-wrap items-end justify-between gap-4 shirorekha pb-3">
        <h1 className="text-[2rem] sm:text-[2.4rem]">{t("discussions_title")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-thread-btn"><Plus size={16} className="mr-1" aria-hidden="true" />{t("start_thread")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("start_thread")}</DialogTitle></DialogHeader>
            <label className="text-sm" htmlFor="thread-title">{t("thread_title_label")}</label>
            <Input id="thread-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="thread-title-input" />
            <label className="text-sm" htmlFor="thread-body">{t("thread_body_label")}</label>
            <Textarea id="thread-body" rows={6} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} data-testid="thread-body-input" />
            <Button onClick={submit} data-testid="submit-thread-btn">{t("publish_thread")}</Button>
          </DialogContent>
        </Dialog>
      </header>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <form role="search" onSubmit={e => { e.preventDefault(); setQuery({ q: q.trim() }); }} className="min-w-0 flex-1 sm:flex-none">
          <label htmlFor="discussions-search" className="sr-only">{t("search_discussions")}</label>
          <Input id="discussions-search" value={q} onChange={e => setQ(e.target.value)} placeholder={t("search_discussions")} className="w-full sm:w-64" data-testid="discussions-search" />
        </form>
        <ul className="flex gap-2" aria-label={t("sort_by")}>
          {[["recent", t("sort_newest")], ["active", t("sort_active")], ["top", t("sort_top")]].map(([s, label]) => (
            <li key={s}><FilterChip active={sort === s} onClick={() => setQuery({ sort: s })} testid={`sort-${s}`}>{label}</FilterChip></li>
          ))}
        </ul>
      </div>

      <ul className="mt-6 divide-y divide-border border-y border-border" data-testid="threads-list">
        {status === "error" && <li className="py-6"><ErrorState onRetry={reload} /></li>}
        {status === "loading" && !data && Array.from({ length: 5 }, (_, i) => <li key={i} className="py-4"><div className="h-14 skel" /></li>)}
        {status === "ok" && items.length === 0 && <li className="py-8 text-muted-foreground">{query ? t("no_discussions_match") : t("no_discussions_first")}</li>}
        {items.map(th => (
          <li key={th.id}>
            <Link to={`/discussions/${th.id}`} data-testid={`thread-${th.id}`} className="group flex items-start gap-4 py-4">
              <span className="flex min-w-[2.5rem] flex-col items-center pt-1 text-primary" aria-label={`${th.upvote_count || 0} ${t("votes")}`}>
                <ArrowUp size={14} aria-hidden="true" /><span className="text-sm font-semibold" aria-hidden="true">{th.upvote_count || 0}</span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[1.1rem] leading-snug line-clamp-2 group-hover:text-primary">{th.title}</span>
                {th.body && <span className="mt-1 block text-muted-foreground line-clamp-2">{th.body}</span>}
                <span className="mt-1.5 flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
                  <span>{th.user_name}</span>
                  <time dateTime={th.created_at}>{formatDate(th.created_at, lang)}</time>
                  <span className="inline-flex items-center gap-1"><MessageSquare size={13} aria-hidden="true" />{t("replies_count").replace("{n}", th.reply_count || 0)}</span>
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {data && <ListPagination page={page} limit={PAGE_SIZE} total={data.total || 0} onPageChange={(p) => setQuery({ page: p > 1 ? String(p) : "" })} testid="threads-pagination" />}
    </div>
  );
}
